import { formatCompactMoney } from '../../lib/currency';
import { dollars } from './Figures';

/**
 * The net cash flow bar drawing shared by the Overview card (`NetCashFlowCard`) and the Cash flow
 * Summary's Twelve months chart (steward-app#2836): one scale, one pair of colours, one zero line.
 * Elements are spans so a button can hold them. A column is the part ABOVE the line, the line, and the part BELOW it; each caller wraps that in
 * its own column (the card's is a plain div with figures under it, the Summary's is a button).
 */

/** px above (and below) the zero line. */
export const NET_HALF = 56;

/** The hatch for a month in progress, in the bar's own colour (`currentColor`). */
export const HATCH = { backgroundImage: 'repeating-linear-gradient(135deg, currentColor 0 2px, transparent 2px 6px)' };

/** The largest absolute net, so every bar shares one scale. 0 when every net is 0. */
export const netScaleMax = (nets: readonly number[]): number => Math.max(0, ...nets.map(n => Math.abs(n)));

/** Bar height in px: 0 for a zero net, otherwise at least 2 so a small month is still visible. */
export function netBarHeight(netCents: number, max: number): number {
  return netCents === 0 || max === 0 ? 0 : Math.max(2, Math.round((Math.abs(netCents) / max) * NET_HALF));
}

/**
 * Bronze for a month that came out ahead, slate for one that did not, the same pairing as the Net
 * Worth chip (never red); the side of the line says it too, so colour is never the only cue.
 * `partial` draws the bar hatched (a month in progress); `null` net draws no bar, only the line.
 */
export function NetBarStack({ netCents, max, partial = false }: { netCents: number | null; max: number; partial?: boolean }) {
  const net = netCents ?? 0;
  const h = netBarHeight(net, max);
  const tone = net >= 0 ? 'text-bronze' : 'text-slate';
  const fill = partial ? `border border-dashed border-current ${tone}` : net >= 0 ? 'bg-bronze' : 'bg-slate';
  const style = { height: h, ...(partial ? HATCH : {}) };
  return (
    <>
      <span className="flex w-full items-end justify-center" style={{ height: NET_HALF }}>
        {net > 0 && <span data-bar="net" className={`block w-[22px] sm:w-[30px] rounded-t-[1px] ${fill}`} style={style} />}
      </span>
      <span className="block h-px w-full bg-line-strong" />
      <span className="flex w-full items-start justify-center" style={{ height: NET_HALF }}>
        {net < 0 && <span data-bar="net" className={`block w-[22px] sm:w-[30px] rounded-b-[1px] ${fill}`} style={style} />}
      </span>
    </>
  );
}

/**
 * `+$3,856`, `-$428`, `$0`: the sign shown both ways, like the Net Worth chip. Signed on the
 * WHOLE-DOLLAR figure shown, and a figure that rounds to zero is plain `$0` (never `+$0` or `-$0`).
 */
export function signed(cents: number): string {
  const whole = Math.round(cents / 100);
  return whole === 0 ? dollars(0) : `${whole > 0 ? '+' : ''}${dollars(whole * 100)}`;
}
/** `+$2.1K`, `-$4.3K`, `+$486`, `$0`, for the label under a bar. */
export function compact(cents: number): string {
  const whole = Math.round(cents / 100);
  return whole === 0 ? formatCompactMoney(0, 'USD') : `${whole > 0 ? '+' : ''}${formatCompactMoney(whole, 'USD')}`;
}
